import { jsPDF } from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import moment from 'moment';

import { ValuationCarFile } from './evaluation.types';
import { ValuationStandardOption, ValuationExtraOption } from '../../interfaces/Case';
import { ITranslatorService } from '../../services/Interfaces/ITranslatorService';
import { loadRobotoFonts } from '../../helpers/pdfFonts';
import { Utils } from '../../helpers/Utils';
import projectLogo from '../../assets/images/logo_carfix_partner.png';
import ImageNotAvailable from '../../assets/images/car-image-not-available.jpg';

// A4 portrait layout (mm)
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 10;
const MARGIN_TOP = 18;
const MARGIN_BOTTOM = 18;
const CONTENT_W = PAGE_W - 2 * MARGIN_X;
const CONTENT_BOTTOM = PAGE_H - MARGIN_BOTTOM;

const FONT = 'Roboto';
type RGB = [number, number, number];
const COLORS: Record<string, RGB> = {
  text: [33, 37, 41],
  muted: [120, 120, 120],
  sectionBg: [216, 232, 245],
  sectionFg: [32, 72, 106],
  border: [220, 220, 220],
  success: [40, 167, 69]
};

const formatNowDateTime = (): string => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy}, ${hh}:${min}`;
};

const formatDbDateTime = (s?: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours() + 3).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const fmtMoney = (n?: number | null): string =>
  Number.isFinite(n as number) ? (n as number).toFixed(2) : '0.00';

type LoadedImage = {
  dataUrl: string;
  width: number;
  height: number;
  format: 'PNG' | 'JPEG';
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });

const decodeImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = src;
  });

const loadImage = async (src: string): Promise<LoadedImage> => {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const dataUrl = await blobToDataUrl(blob);
  const img = await decodeImage(dataUrl);
  const format = blob.type === 'image/png' ? 'PNG' : 'JPEG';
  return { dataUrl, width: img.width, height: img.height, format };
};

const loadImageWithFallback = async (src: string | null | undefined): Promise<LoadedImage> => {
  const candidates = [src, ImageNotAvailable].filter(Boolean) as string[];
  for (const candidate of candidates) {
    try {
      return await loadImage(candidate);
    } catch {
      // try next
    }
  }
  // Last resort: 1x1 transparent placeholder so addImage doesn't blow up.
  return {
    dataUrl:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=',
    width: 1,
    height: 1,
    format: 'PNG'
  };
};

export type GenerateEvaluationPdfOptions = {
  carDetails: ValuationCarFile;
  translatorService: ITranslatorService;
};

export const generateEvaluationReportPdf = async ({
  carDetails,
  translatorService
}: GenerateEvaluationPdfOptions): Promise<{ blob: Blob; filename: string }> => {
  const t = (key: string, fallback: string) => translatorService.Tranlate(key, fallback);

  const fonts = await loadRobotoFonts();

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  pdf.addFileToVFS('Roboto-Regular.ttf', fonts.regular);
  pdf.addFont('Roboto-Regular.ttf', FONT, 'normal');
  pdf.addFileToVFS('Roboto-Bold.ttf', fonts.bold);
  pdf.addFont('Roboto-Bold.ttf', FONT, 'bold');
  pdf.setFont(FONT, 'normal');

  const [logoImg, carImg] = await Promise.all([
    loadImage(projectLogo).catch(() => loadImageWithFallback(null)),
    loadImageWithFallback(carDetails.imageUrl)
  ]);

  const generatedAt = formatNowDateTime();
  const reportTitle = t('VALUATION_REPORT_TITLE', 'Raport evaluare');
  const inclVatLabel = t('VALUATION_REPORT_INCLUDING_VAT_LABEL', 'brut');

  let y = MARGIN_TOP;

  const ensureSpace = (need: number) => {
    if (y + need > CONTENT_BOTTOM) {
      pdf.addPage();
      y = MARGIN_TOP;
    }
  };

  const setText = (
    size: number,
    weight: 'normal' | 'bold' = 'normal',
    color: RGB = COLORS.text
  ) => {
    pdf.setFont(FONT, weight);
    pdf.setFontSize(size);
    pdf.setTextColor(color[0], color[1], color[2]);
  };

  const drawSectionTitleAt = (x: number, yPos: number, width: number, text: string): number => {
    const h = 12;
    pdf.setFillColor(COLORS.sectionBg[0], COLORS.sectionBg[1], COLORS.sectionBg[2]);
    pdf.roundedRect(x, yPos, width, h, 1, 1, 'F');
    setText(10, 'bold', COLORS.sectionFg);
    pdf.text(text.toUpperCase(), x + 3, yPos + h / 2 + 1.6);
    setText(10, 'normal');
    return yPos + h + 2.5;
  };

  const drawTopBar = () => {
    const logoH = 12.5;
    const logoW = (logoImg.width * logoH) / logoImg.height || 30;
    pdf.addImage(logoImg.dataUrl, logoImg.format, MARGIN_X, y, logoW, logoH);

    setText(16, 'bold');
    const titleText = reportTitle.toUpperCase();
    const titleW = pdf.getTextWidth(titleText);
    pdf.text(titleText, PAGE_W - MARGIN_X - titleW, y + logoH * 0.75);

    pdf.setDrawColor(COLORS.border[0], COLORS.border[1], COLORS.border[2]);
    pdf.setLineWidth(0.2);
    pdf.line(MARGIN_X, y + logoH + 2, PAGE_W - MARGIN_X, y + logoH + 2);
    y += logoH + 6;
    setText(10, 'normal');
  };

  const drawVehicleTitle = () => {
    const parts = [carDetails.make, carDetails.model, carDetails.version]
      .filter((p) => p && String(p).trim().length > 0)
      .join(' ');
    const km = carDetails.numberOfKilometers ?? 0;
    const text = `${parts}, ${km} km`.trim();
    const titleSize = 18;
    const titleLineH = 8;
    setText(titleSize, 'normal');
    const lines = pdf.splitTextToSize(text, CONTENT_W) as string[];
    const blockH = lines.length * titleLineH + 2;
    ensureSpace(blockH);
    for (let i = 0; i < lines.length; i += 1) {
      pdf.text(lines[i], MARGIN_X, y + titleLineH * (i + 1) - 1);
    }
    y += blockH;
  };

  const drawSpecsAndImage = () => {
    const colW = (CONTENT_W - 4) / 2;
    const leftX = MARGIN_X;
    const rightX = MARGIN_X + colW + 4;
    const startY = y;

    let leftY = drawSectionTitleAt(
      leftX,
      startY,
      colW,
      t('VALUATION_REPORT_CAR_DETAILS_LABEL', 'Specificatii autovehicul')
    );

    const specsRows: RowInput[] = [
      [t('CAR_DETAILS_CHASIS_CODE', 'Serie sasiu'), carDetails.vin || '-'],
      [t('VALUATION_REPORT_MAKE_LABEL', 'Producator'), carDetails.make || '-'],
      [t('VALUATION_REPORT_MODEL_LABEL', 'Model'), carDetails.model || '-'],
      [t('VALUATION_REPORT_VERSION_LABEL', 'Versiune'), carDetails.version || '-'],
      [t('VALUATION_REPORT_EQUIP_LABEL', 'Echipare'), carDetails.equipmentVersion || '-'],
      [t('VALUATION_REPORT_FUEL_LABEL', 'Combustibil'), (carDetails as any).fuelType || '-'],
      [
        t('VALUATION_REPORT_GEARBOX_LABEL', 'Cutie de viteze'),
        (carDetails as any).transmissionType || '-'
      ],
      [
        t('CASE_VALUATION_POWER_LABEL', 'Putere'),
        carDetails.horsePower != null
          ? `${Utils.convertHpToKw(carDetails.horsePower)} kW`
          : '-'
      ],
      [t('VALUATION_REPORT_BODY_TYPE_LABEL', 'Caroserie'), (carDetails as any).bodyType || '-'],
      [
        t('VALUATION_REPORT_DOORS_NUMBER_LABEL', 'Numar usi'),
        String((carDetails as any).nrOfDoors ?? '-')
      ],
      [t('CAR_DETAILS_COLOR', 'Culoare'), carDetails.color || '-'],
      [
        t('CAR_DETAILS_DATE_OF_FABRICATION', 'Data fabricatie'),
        carDetails.fabricationDate
          ? new Date(carDetails.fabricationDate).toLocaleDateString('en-GB')
          : '-'
      ],
      [
        t('VALUATION_REGISTRATION_DATE_LABEL', 'Data primei inmatriculari'),
        carDetails.registrationDate ? moment(carDetails.registrationDate).format('MM/YYYY') : '-'
      ]
    ];

    autoTable(pdf, {
      body: specsRows,
      startY: leftY,
      margin: { left: leftX, right: PAGE_W - leftX - colW, top: MARGIN_TOP, bottom: MARGIN_BOTTOM },
      tableWidth: colW,
      styles: {
        font: FONT,
        fontSize: 10,
        cellPadding: { top: 0.8, bottom: 0.8, left: 1.5, right: 1.5 },
        lineWidth: 0,
        textColor: COLORS.text,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: colW * 0.5 },
        1: { cellWidth: colW * 0.5 }
      },
      theme: 'plain'
    });
    leftY = (pdf as any).lastAutoTable.finalY;

    // Right column: car image, vertically centered against the left column height
    const imgBoxH = leftY - startY;
    const imgBoxW = colW;
    const ratio = carImg.width / carImg.height || 16 / 9;
    let imgW = imgBoxW - 4;
    let imgH = imgW / ratio;
    if (imgH > imgBoxH - 4) {
      imgH = imgBoxH - 4;
      imgW = imgH * ratio;
    }
    const imgX = rightX + (imgBoxW - imgW) / 2;
    const imgY = startY + (imgBoxH - imgH) / 2;
    pdf.addImage(carImg.dataUrl, carImg.format, imgX, imgY, imgW, imgH);

    y = leftY + 4;
  };

  const drawPricesAndValues = () => {
    const colW = (CONTENT_W - 4) / 2;
    const leftX = MARGIN_X;
    const rightX = MARGIN_X + colW + 4;
    const startY = y;

    // Left section: Detalii evaluare
    let leftY = drawSectionTitleAt(
      leftX,
      startY,
      colW,
      t('VALUATION_REPORT_CAR_PRICES_LABEL', 'Detalii evaluare')
    );

    const detailsRows: RowInput[] = [
      [
        t('VALUATION_REPORT_VALUATIO_NUMBER', 'Nr. evaluare'),
        carDetails.id ? `VALUATION-${carDetails.id}` : ''
      ],
      [
        t('VALUATION_REPORT_VALUATION_DATE', 'Data evaluare'),
        carDetails.dateCreation ? formatDbDateTime(carDetails.dateCreation) : ''
      ],
      [
        t('VALUATION_REPORT_BASE_PRICE_LABEL', 'Pret masina noua'),
        `${fmtMoney(carDetails.priceAsNew)}€ ${inclVatLabel}`
      ],
      [
        t('VALUATION_REPORT_OPTIONS_PRICE_LABEL', 'Pret optionale extra'),
        `${fmtMoney(carDetails.extraOptionsPrice)}€ ${inclVatLabel}`
      ],
      [
        t('VALUATION_REPORT_TOTAL_LABEL', 'Pret total masina noua'),
        `${fmtMoney(
          (carDetails.priceAsNew ?? 0) + (carDetails.extraOptionsPrice ?? 0)
        )}€ ${inclVatLabel}`
      ]
    ];
    autoTable(pdf, {
      body: detailsRows,
      startY: leftY,
      margin: { left: leftX, right: PAGE_W - leftX - colW, top: MARGIN_TOP, bottom: MARGIN_BOTTOM },
      tableWidth: colW,
      styles: {
        font: FONT,
        fontSize: 10,
        cellPadding: { top: 0.8, bottom: 0.8, left: 1.5, right: 1.5 },
        lineWidth: 0,
        textColor: COLORS.text,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: colW * 0.5 },
        1: { cellWidth: colW * 0.5 }
      },
      theme: 'plain'
    });
    leftY = (pdf as any).lastAutoTable.finalY;

    // Right section: Valori estimate
    let rightY = drawSectionTitleAt(
      rightX,
      startY,
      colW,
      t('VALUATION_REPORT_CAR_DETAILS_ESTIMATIONS_LABEL', 'Valori estimate')
    );
    const valuesRows: RowInput[] = [
      [
        t('VALUATION_REPORT_TRADE_IN_PRICE_LABEL', 'Trade-in price'),
        `${fmtMoney(carDetails.tradeInPrice)}€ ${inclVatLabel}`
      ],
      [
        t('VALUATION_REPORT_OFFER_PRICE_LABEL', 'Offer price'),
        `${fmtMoney(carDetails.offerPrice)}€ ${inclVatLabel}`
      ],
      [
        t('VALUATION_REPORT_RETAIL_PRICE_LABEL', 'Retail price'),
        `${fmtMoney(carDetails.retailPrice)}€ ${inclVatLabel}`
      ]
    ];
    autoTable(pdf, {
      body: valuesRows,
      startY: rightY,
      margin: { left: rightX, right: MARGIN_X, top: MARGIN_TOP, bottom: MARGIN_BOTTOM },
      tableWidth: colW,
      styles: {
        font: FONT,
        fontSize: 10,
        cellPadding: { top: 0.8, bottom: 0.8, left: 1.5, right: 1.5 },
        lineWidth: 0,
        textColor: COLORS.text,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: colW * 0.5 },
        1: { cellWidth: colW * 0.5 }
      },
      theme: 'plain'
    });
    rightY = (pdf as any).lastAutoTable.finalY;

    y = Math.max(leftY, rightY) + 4;
  };

  // Renders bullets in two truly independent columns. Even-indexed bullets go to
  // the left column, odd-indexed to the right. Each column has its own Y cursor
  // and page counter — a tall multi-line bullet on one side never creates a gap
  // on the other side. The left column is drawn first (may add pages); the right
  // column reuses those pages via setPage() and only calls addPage() if it
  // outgrows them.
  const drawBulletList = (texts: string[], emptyLabel: string) => {
    if (texts.length === 0) {
      ensureSpace(6);
      setText(10, 'normal', COLORS.muted);
      pdf.text(emptyLabel, MARGIN_X + 2, y + 4);
      y += 7;
      setText(10, 'normal', COLORS.text);
      return;
    }

    const fontSize = 10;
    const lineHeight = 4.5;
    const itemSpacing = 0.6;
    const colGap = 4;
    const colW = (CONTENT_W - colGap) / 2;
    const bulletIndent = 4;
    const textW = colW - bulletIndent;
    const leftX = MARGIN_X + 2;
    const rightX = MARGIN_X + colW + colGap + 2;

    setText(fontSize, 'normal', COLORS.text);

    // Pre-compute wrapped line counts for every bullet so we can split by
    // total height rather than by count. This guarantees both columns have
    // roughly the same amount of text regardless of which bullets are long.
    const allWrapped = texts.map((t) => pdf.splitTextToSize(t, textW) as string[]);
    const totalLines = allWrapped.reduce((acc, lines) => acc + lines.length, 0);

    let cumLines = 0;
    let splitIdx = Math.ceil(texts.length / 2); // fallback: count-based
    for (let k = 0; k < texts.length; k++) {
      cumLines += allWrapped[k].length;
      if (cumLines * 2 >= totalLines) {
        splitIdx = k + 1;
        break;
      }
    }

    const leftTexts = texts.slice(0, splitIdx);
    const rightTexts = texts.slice(splitIdx);

    const startPage = pdf.getNumberOfPages();
    const startY = y;

    // Renders one column independently. When it needs the next page it calls
    // setPage() if that page already exists (created by the other column pass),
    // or addPage() to append a fresh page at the document end.
    const renderColumn = (colTexts: string[], xBase: number): { endPage: number; endY: number } => {
      let pg = startPage;
      let cy = startY;
      pdf.setPage(pg);

      for (const text of colTexts) {
        const lines = pdf.splitTextToSize(text, textW) as string[];
        for (let li = 0; li < lines.length; li += 1) {
          if (cy + lineHeight > CONTENT_BOTTOM) {
            pg += 1;
            if (pg <= pdf.getNumberOfPages()) {
              pdf.setPage(pg);
            } else {
              pdf.addPage(); // appends at document end
            }
            cy = MARGIN_TOP;
          }
          if (li === 0) pdf.text('\u2022', xBase, cy + lineHeight - 1);
          pdf.text(lines[li], xBase + bulletIndent, cy + lineHeight - 1);
          cy += lineHeight;
        }
        cy += itemSpacing;
      }

      return { endPage: pg, endY: cy };
    };

    const leftResult = renderColumn(leftTexts, leftX);
    const rightResult = renderColumn(rightTexts, rightX);

    // Leave the document positioned on the last page used by either column.
    const finalPage = Math.max(leftResult.endPage, rightResult.endPage);
    pdf.setPage(finalPage);
    y =
      leftResult.endPage === rightResult.endPage
        ? Math.max(leftResult.endY, rightResult.endY)
        : leftResult.endPage > rightResult.endPage
        ? leftResult.endY
        : rightResult.endY;
    y += 2;
  };

  const drawStandardOptions = () => {
    ensureSpace(15);
    y = drawSectionTitleAt(
      MARGIN_X,
      y,
      CONTENT_W,
      `${t('VALUATION_CAR_FILE_STANDARD_OPTIONS', 'Optionale standard selectate')}`
    );
    const options: ValuationStandardOption[] = carDetails.options?.standardOptions ?? [];
    const texts = options.map((o) => o?.content ?? '').filter((s) => s.trim().length > 0);
    drawBulletList(texts, t('VALUATION_CAR_FILE_EMPTY_LIST', 'Nu exista optiuni selectate'));
  };

  const drawExtraOptions = () => {
    ensureSpace(15);
    y = drawSectionTitleAt(
      MARGIN_X,
      y,
      CONTENT_W,
      `${t('VALUATION_CAR_FILE_EXTRA_OPTIONS', 'Optionale extra selectate')}`
    );
    const options: ValuationExtraOption[] = carDetails.options?.extraOptions ?? [];
    const texts = options.map(
      (o) => `[${o?.optionCode ?? ''}] ${o?.description ?? ''} - ${o?.price ?? 0}€`
    );
    drawBulletList(texts, t('VALUATION_CAR_FILE_EMPTY_LIST', 'Nu exista optiuni selectate'));
  };

  const decoratePages = () => {
    const total = pdf.getNumberOfPages();
    for (let i = 1; i <= total; i += 1) {
      pdf.setPage(i);
      setText(8, 'normal', COLORS.muted);
      pdf.text(reportTitle, MARGIN_X, 8);
      pdf.text(generatedAt, PAGE_W - MARGIN_X, 8, { align: 'right' });
      pdf.text(`${i}/${total}`, PAGE_W - MARGIN_X, PAGE_H - 6, { align: 'right' });
    }
  };

  drawTopBar();
  drawVehicleTitle();
  drawSpecsAndImage();
  drawPricesAndValues();
  drawStandardOptions();
  drawExtraOptions();
  decoratePages();

  const blob = pdf.output('blob');
  const make = (carDetails.make ?? '').replace(/\s+/g, '_') || 'auto';
  const model = (carDetails.model ?? '').replace(/\s+/g, '_') || 'model';
  const dateStr = carDetails.date ? new Date(carDetails.date).toLocaleDateString('en-GB') : '';
  const filename = `Raport_evaluare_${make}_${model}${dateStr ? `_${dateStr}` : ''}.pdf`;

  return { blob, filename };
};
